import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import './TilemapVisualization.css';

/**
 * Get tile vertices for SVG polygon rendering
 */
const getTileVertices = (width, height, tileShape = 'rect') => {
  const hw = width / 2;
  const hh = height / 2;
  
  if (tileShape === 'rect') {
    return `${-hw},${-hh} ${hw},${-hh} ${hw},${hh} ${-hw},${hh}`;
  } else if (tileShape === 'hex-base') {
    const q = hh / Math.sqrt(3);
    return `${-q},${hh} ${q},${hh} ${2*q},0 ${q},${-hh} ${-q},${-hh} ${-2*q},0`;
  } else if (tileShape === 'hex-side') {
    const q = hw / Math.sqrt(3);
    return `${-hw},${q} 0,${2*q} ${hw},${q} ${hw},${-q} 0,${-2*q} ${-hw},${-q}`;
  }
  
  return `${-hw},${-hh} ${hw},${-hh} ${hw},${hh} ${-hw},${hh}`;
};

/**
 * Parse map analysis data to extract severity values and legend
 */
const parseMapAnalysisData = (mapAnalysisData) => {
  if (!mapAnalysisData) return { severityMap: new Map(), legend: [] };
  
  try {
    let parsedData = mapAnalysisData;
    
    if (typeof mapAnalysisData === 'string') {
      parsedData = JSON.parse(mapAnalysisData);
    } else if (mapAnalysisData.content) {
      parsedData = typeof mapAnalysisData.content === 'string' 
        ? JSON.parse(mapAnalysisData.content) 
        : mapAnalysisData.content;
    }
    
    const dataArray = parsedData.data || [];
    const legend = parsedData.legend || [];
    
    const severityMap = new Map();
    dataArray.forEach(item => {
      const adminCode = item.admin1_pcode || item.adm1_pcode || item.pcode;
      if (adminCode) {
        const value = item.value !== undefined ? item.value : 
                     (item.category !== undefined ? item.category : 
                     (item.severity_score !== undefined ? item.severity_score : null));
        if (value !== null) {
          severityMap.set(adminCode, {
            value,
            rationale: item.rationale || item.description || '',
            name: item.admin1_name || item.adm1_name || ''
          });
        }
      }
    });
    
    return { severityMap, legend };
  } catch (e) {
    console.error('Error parsing map analysis data:', e);
    return { severityMap: new Map(), legend: [] };
  }
};

/**
 * Get color for a severity value from legend
 */
const getColorFromLegend = (value, legend) => {
  if (!legend || legend.length === 0) return '#e0e0e0';
  
  const legendItem = legend.find(item => {
    if (item.id !== undefined && String(item.id) === String(value)) return true;
    if (item.value !== undefined && String(item.value) === String(value)) return true;
    if (item.category !== undefined && String(item.category) === String(value)) return true;
    if (item.title === value) return true;
    return false;
  });
  
  return legendItem?.color || '#e0e0e0';
};

/**
 * TilemapVisualization Component
 * 
 * Renders pre-generated tiles from the database with colors based on mapAnalysisData.
 * This is a lightweight component - all tile positioning is pre-computed in admin.
 */
const TilemapVisualization = memo(({
  viewId,
  mapAnalysisData = null,
  hoveredSeverity = null,
  hoveredPcode = null,
  isMouseOverLegend = false,
  onTileHover = null,
  onTileClick = null,
  mapUpdatedAt = null,
  width = '100%',
  height = 400,
  compact = false
}) => {
  const [tilemapData, setTilemapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredTile, setHoveredTile] = useState(null);
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // Fetch tilemap data
  useEffect(() => {
    if (!viewId) {
      setLoading(false);
      return;
    }
    
    const fetchTilemapData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/tilemap/${viewId}/data`);
        if (response.ok) {
          const data = await response.json();
          if (data.enabled && data.tiles && data.tiles.length > 0) {
            setTilemapData(data);
          } else {
            setTilemapData(null);
          }
        } else {
          setTilemapData(null);
        }
      } catch (err) {
        console.error('Error fetching tilemap data:', err);
        setError('Failed to load tilemap');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTilemapData();
  }, [viewId]);
  
  // Parse map analysis data for coloring and extract full data
  const { severityMap, legend, fullDataMap } = useMemo(() => {
    if (!mapAnalysisData) return { severityMap: new Map(), legend: [], fullDataMap: new Map() };
    
    try {
      let parsedData = mapAnalysisData;
      
      if (typeof mapAnalysisData === 'string') {
        parsedData = JSON.parse(mapAnalysisData);
      } else if (mapAnalysisData.content) {
        parsedData = typeof mapAnalysisData.content === 'string' 
          ? JSON.parse(mapAnalysisData.content) 
          : mapAnalysisData.content;
      }
      
      const dataArray = parsedData.data || [];
      const legend = parsedData.legend || [];
      
      const severityMap = new Map();
      const fullDataMap = new Map();
      
      dataArray.forEach(item => {
        const adminCode = item.admin1_pcode || item.adm1_pcode || item.pcode;
        if (adminCode) {
          const value = item.value !== undefined ? item.value : 
                       (item.category !== undefined ? item.category : 
                       (item.severity_score !== undefined ? item.severity_score : null));
          if (value !== null) {
            severityMap.set(adminCode, {
              value,
              rationale: item.rationale || item.description || '',
              name: item.admin1_name || item.adm1_name || ''
            });
            // Store full item data for rich tooltips
            fullDataMap.set(adminCode, item);
          }
        }
      });
      
      return { severityMap, legend, fullDataMap };
    } catch (e) {
      console.error('Error parsing map analysis data:', e);
      return { severityMap: new Map(), legend: [], fullDataMap: new Map() };
    }
  }, [mapAnalysisData]);
  
  // Handle tile hover
  const handleTileMouseEnter = useCallback((tile, event) => {
    if (isMouseOverLegend) return;
    
    setHoveredTile(tile.adm1_pcode);
    
    const severity = severityMap.get(tile.adm1_pcode);
    const fullData = fullDataMap.get(tile.adm1_pcode);
    const tooltipData = {
      name: tile.adm1_name || tile.adm1_pcode,
      code: tile.adm1_pcode,
      ...severity,
      ...fullData
    };
    
    setTooltipContent(tooltipData);
    setTooltipPosition({
      x: event.clientX,
      y: event.clientY
    });
    
    if (onTileHover) {
      onTileHover(tile.adm1_pcode, tooltipData);
    }
  }, [severityMap, fullDataMap, onTileHover, isMouseOverLegend]);
  
  const handleTileMouseLeave = useCallback(() => {
    setHoveredTile(null);
    setTooltipContent(null);
    if (onTileHover) {
      onTileHover(null, null);
    }
  }, [onTileHover]);
  
  const handleTileClick = useCallback((tile) => {
    if (onTileClick) {
      const severity = severityMap.get(tile.adm1_pcode);
      onTileClick(tile.adm1_pcode, {
        name: tile.adm1_name || tile.adm1_pcode,
        code: tile.adm1_pcode,
        ...severity
      });
    }
  }, [severityMap, onTileClick]);
  
  // Check if tile should be highlighted based on legend hover
  const isTileHighlighted = useCallback((tile) => {
    if (!isMouseOverLegend || hoveredSeverity === null) return false;
    
    const severity = severityMap.get(tile.adm1_pcode);
    if (!severity) return false;
    
    // Check if tile's severity matches hovered legend item
    const legendItem = legend.find(item => {
      if (item.id !== undefined && String(item.id) === String(hoveredSeverity)) return true;
      if (item.value !== undefined && String(item.value) === String(hoveredSeverity)) return true;
      if (item.title === hoveredSeverity) return true;
      return false;
    });
    
    if (!legendItem) return false;
    
    return String(severity.value) === String(legendItem.id) ||
           String(severity.value) === String(legendItem.value) ||
           severity.value === legendItem.title;
  }, [isMouseOverLegend, hoveredSeverity, severityMap, legend]);
  
  // Render nothing if no tilemap or not enabled
  if (loading) {
    return (
      <div className={`tilemap-loading ${compact ? 'compact' : ''}`}>
        <div className="tilemap-spinner" />
        {!compact && <span>Loading tilemap...</span>}
      </div>
    );
  }
  
  if (error) {
    if (compact) return null;
    return (
      <div className="tilemap-error">
        {error}
      </div>
    );
  }
  
  if (!tilemapData || !tilemapData.tiles || tilemapData.tiles.length === 0) {
    return null;
  }
  
  const { tiles, config } = tilemapData;
  const tileShape = config?.tileShape || 'rect';
  const showLabels = !compact && config?.showLabels !== false;
  
  // Calculate SVG viewBox dimensions
  const svgWidth = typeof width === 'number' ? width : 400;
  const svgHeight = typeof height === 'number' ? height : 400;
  const margin = compact ? 2 : 20;
  
  return (
    <div className="tilemap-container" style={{ width, height }}>
      <svg 
        className="tilemap-svg"
        width="100%" 
        height="100%" 
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {tiles.map((tile) => {
          const x = margin + tile.x * (svgWidth - 2 * margin);
          const y = margin + tile.y * (svgHeight - 2 * margin);
          const w = tile.width * (svgWidth - 2 * margin);
          const h = tile.height * (svgHeight - 2 * margin);
          
          // Get color from map analysis data
          const severity = severityMap.get(tile.adm1_pcode);
          let fillColor = '#e0e0e0'; // Default gray
          let fillOpacity = 0.7;
          
          if (severity) {
            fillColor = getColorFromLegend(severity.value, legend);
          }
          
          // Handle hover states - check both local hover and external hoveredPcode
          const isHoveredLocally = hoveredTile === tile.adm1_pcode;
          const isHoveredByPcode = hoveredPcode && (
            tile.adm1_pcode === hoveredPcode || 
            tile.adm1_pcode?.endsWith(hoveredPcode) || 
            hoveredPcode?.endsWith(tile.adm1_pcode)
          );
          const isHovered = isHoveredLocally || isHoveredByPcode;
          const isLegendHighlighted = isTileHighlighted(tile);
          
          if (isMouseOverLegend && !isLegendHighlighted) {
            fillOpacity = 0.3;
          } else if (isHovered || isLegendHighlighted) {
            fillOpacity = 0.9;
          }
          
          const vertices = getTileVertices(w, h, tileShape);
          const labelFontSize = Math.min(w / 5, h / 3, 12);
          
          return (
            <g 
              key={tile.adm1_pcode}
              className={`tilemap-tile ${isHovered ? 'hovered' : ''}`}
              transform={`translate(${x}, ${y})`}
              onMouseEnter={(e) => handleTileMouseEnter(tile, e)}
              onMouseLeave={handleTileMouseLeave}
              onClick={() => handleTileClick(tile)}
              style={{ cursor: 'pointer' }}
            >
              <polygon
                points={vertices}
                fill={fillColor}
                fillOpacity={fillOpacity}
                stroke={isHovered ? '#333' : '#fff'}
                strokeWidth={isHovered ? 2 : 1}
              />
              {showLabels && labelFontSize >= 6 && (
                <text
                  x={0}
                  y={0}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={labelFontSize}
                  fill="#333"
                  pointerEvents="none"
                >
                  {(tile.adm1_name || tile.adm1_pcode || '').slice(0, 8)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      
      {/* Tooltip - identical to Admin1Layer tooltip */}
      {tooltipContent && !isMouseOverLegend && (() => {
        // Build tooltip HTML exactly like Admin1Layer
        let severityInfo = '';
        let rationaleInfo = '';
        let additionalInfo = '';
        let timestampHtml = '';
        
        // Get severity info with color box
        if (tooltipContent.value !== undefined) {
          const severityColor = getColorFromLegend(tooltipContent.value, legend);
          const legendItem = legend.find(item => {
            if (item.id !== undefined && String(item.id) === String(tooltipContent.value)) return true;
            if (item.value !== undefined && String(item.value) === String(tooltipContent.value)) return true;
            if (item.category !== undefined && String(item.category) === String(tooltipContent.value)) return true;
            return item.title === tooltipContent.value || item.category === tooltipContent.value;
          });
          
          const categoryLabel = legendItem?.title || legendItem?.description?.split(':')[0].trim() || `${tooltipContent.value}`;
          const colorBox = severityColor ? 
            `<div style="opacity: 0.30333; display: inline-block; width: 12px; height: 12px; background-color: ${severityColor}; border-radius: 1px; margin-right: 8px; vertical-align: middle; margin-top: 5px; box-shadow: 0 1px 2px rgba(0,0,0,0.01);"></div>` : 
            '';
          
          severityInfo = `<div style="display: flex; align-items: center; margin: 4px 0; padding: 4px; margin-top: 1px; background-color: rgba(255,255,255,0.95); border-radius: 4px;">
            ${colorBox}<span style="font-weight: 500; font-size: var(--font-size-sm);">${categoryLabel}</span>
          </div>`;
        }
        
        // Get rationale with HTML escaping
        if (tooltipContent.rationale) {
          const escapeHTML = (str) => {
            let cleanStr = str.replace(/\[\d+\]/g, '');
            cleanStr = cleanStr.replace(/\s+/g, ' ').trim();
            return cleanStr
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#039;')
              .replace(/\n/g, '<br>');
          };
          rationaleInfo = escapeHTML(tooltipContent.rationale);
        }
        
        // Add additional info fields
        if (tooltipContent.population) {
          additionalInfo += `<div><strong>Population:</strong> ${tooltipContent.population.toLocaleString()}</div>`;
        }
        
        if (tooltipContent.confidence_level) {
          const confidenceText = tooltipContent.confidence_level.replace('Confidence level - ', '');
          const confidenceColor = confidenceText.toLowerCase() === 'high' ? '#28a745' : 
                                 confidenceText.toLowerCase() === 'medium' ? '#ffc107' : '#dc3545';
          additionalInfo += `<div><strong>Confidence:</strong> <span style="color: ${confidenceColor}; font-weight: bold;">${confidenceText}</span></div>`;
        }
        
        if (tooltipContent.evidence) {
          const evidenceText = tooltipContent.evidence.length > 150 ? 
            `${tooltipContent.evidence.substring(0, 150)}...` : tooltipContent.evidence;
          additionalInfo += `<div style="margin-top: 4px;"><strong>Evidence:</strong> <span style="font-style: italic; color: #666;">${evidenceText}</span></div>`;
        }
        
        if (tooltipContent.sources) {
          const sourcesText = tooltipContent.sources.length > 100 ? 
            `${tooltipContent.sources.substring(0, 100)}...` : tooltipContent.sources;
          additionalInfo += `<div style="margin-top: 4px;"><strong>Sources:</strong> <span style="font-size: 0.85em; color: #666;">${sourcesText}</span></div>`;
        }
        
        // Add timestamp
        const hasActualData = severityInfo || rationaleInfo || additionalInfo;
        if (mapUpdatedAt && hasActualData) {
          try {
            const timeAgo = formatDistanceToNow(mapUpdatedAt, { addSuffix: true }).replace('about ', '');
            timestampHtml = `
              <div style="display: flex; align-items: center; margin-top: 8px; background-color: rgba(0, 0, 0, 0.03); border-radius: 12px; padding: 2px 8px; border: 1px solid rgba(0, 0, 0, 0.05); width: fit-content;">
                <span style="margin-right: 4px; display: inline-flex;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </span>
                <span style="font-size: var(--font-size-md); opacity: 0.7; font-style: italic;">${timeAgo}</span>
              </div>`;
          } catch (e) {
            console.error('Error formatting date in tooltip:', e);
          }
        }
        
        const tooltipHTML = `
          <div class="tooltip-title">${tooltipContent.name}</div>
          ${tooltipContent.code ? `<div><strong>Code:</strong> ${tooltipContent.code}</div>` : ''}
          ${severityInfo}
          ${rationaleInfo ? `
            <div class="tooltip-rationale">
              <strong>Rationale:</strong>
              <div class="rationale-content">${rationaleInfo}</div>
            </div>
          ` : ''}
          ${additionalInfo}
          ${timestampHtml}
        `;
        
        return (
          <div 
            className="tilemap-tooltip"
            style={{
              left: tooltipPosition.x + 15,
              top: tooltipPosition.y + 15
            }}
            dangerouslySetInnerHTML={{ __html: tooltipHTML }}
          />
        );
      })()}
    </div>
  );
});

TilemapVisualization.displayName = 'TilemapVisualization';

export default TilemapVisualization;
